import {FC, useEffect, useMemo, useState} from 'react'
import {Card, Row, Col, Form, Button, Badge, Spinner, Table, InputGroup} from 'react-bootstrap'
import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../../modules/auth/core/AbilityContext'
import DataTable from '../../../../../ui-kit/DataTable/DataTable'
import {Toast} from '../../../../../ui-kit'
import useAxiosPrivate from '../../../../libs/useAxiosPrivate'
import {formatNumber} from '../../../../libs/misc'
import {
  getCGECommodityTariffs,
  getCGETariffEntities,
  getCGECommodityRollupPreview,
} from '../core/_requests'
import {
  CGEMetadata,
  CGECommodityRow,
  CGECommodityPairRequest,
  CGETariffEntity,
  CGETariffEntitiesResponse,
  CGESectorRollup,
  CGEShock,
} from '../core/_models'
import RegionDropdown, {RegionSection, regionFlagIcon} from './RegionDropdown'

// These 8 economies each map to their own CGE engine region. Every other
// TINA economy resolves to one of the engine's 2 aggregate regions (row /
// seasia). See the "South East Asia" / "Rest of World" sections built
// below from GET /cge/tariff-entities.
const CGE_TARIFF_ENTITIES: CGETariffEntity[] = [
  'BGD',
  'CAN',
  'CHN',
  'EUN',
  'GBR',
  'IND',
  'JPN',
  'USA',
]
const ENTITY_LABELS: Record<CGETariffEntity, string> = {
  BGD: 'Bangladesh',
  CAN: 'Canada',
  CHN: 'China',
  EUN: 'European Union',
  GBR: 'United Kingdom',
  IND: 'India',
  JPN: 'Japan',
  USA: 'United States',
}
const TARIFF_ENTITY_OPTIONS = CGE_TARIFF_ENTITIES.map((c) => ({
  code: c,
  name: `${ENTITY_LABELS[c]} (${c})`,
}))

// Client-side mirror of the backend's resolveGtapRegion, scoped to just
// telling apart "which group does this iso3 belong to" for dropdown
// filtering, doesn't need the actual engine region codes, just a key that's
// unique per core entity and shared across an aggregate's members.
const regionGroupKey = (iso3: string, seasiaIso3s: Set<string>) =>
  CGE_TARIFF_ENTITIES.includes(iso3) ? iso3 : seasiaIso3s.has(iso3) ? 'seasia' : 'row'

type CommodityRowState = CGECommodityRow & {new_rate_pct: number}

type StagedPair = {
  id: number
  reporter: CGETariffEntity
  partner: CGETariffEntity
  year: number
  touched_rows: {hs: string; new_rate_pct: number}[]
}

type DirectShockRow = {
  id: number
  good: string
  source: string
  dest: string
  tms_power_change_pct: number
}

type CGECommodityShockFormProps = {
  metadata: CGEMetadata | null
  onSubmit: (req: {
    closure: string
    method: 'johansen' | 'gragg'
    pairs: CGECommodityPairRequest[]
    direct_shocks: CGEShock[]
  }) => void
}

const powerChangePct = (oldRate: number, newRate: number) =>
  100 * ((1 + newRate / 100) / (1 + oldRate / 100) - 1)

// Trade-value-weighted rollup, mirrors the server-side formula in
// tina-data's routes/cms/cge.js (rollupToSectorShocks), shown live here
// purely for user feedback; the server always recomputes authoritatively
// from its own DB before calling /simulate.
function clientRollup(rows: CommodityRowState[]) {
  const bySector = new Map<string, {label: string; oldW: number; newW: number; w: number}>()
  for (const r of rows) {
    if (!r.sector_code || !r.export_value) continue
    const entry = bySector.get(r.sector_code) || {
      label: r.sector_label ?? r.sector_code,
      oldW: 0,
      newW: 0,
      w: 0,
    }
    entry.oldW += r.old_rate_pct * r.export_value
    entry.newW += r.new_rate_pct * r.export_value
    entry.w += r.export_value
    bySector.set(r.sector_code, entry)
  }
  return (
    Array.from(bySector.entries())
      .filter(([, v]) => v.w > 0)
      .map(([sector, v]) => ({
        sector,
        sector_label: v.label,
        export_value: v.w,
        old_rate_pct: v.oldW / v.w,
        new_rate_pct: v.newW / v.w,
        tms_power_change_pct: powerChangePct(v.oldW / v.w, v.newW / v.w),
      }))
      // Biggest export sector first, lets the user see at a glance what the
      // reporter's largest exports to this partner are.
      .sort((a, b) => b.export_value - a.export_value)
  )
}

let nextPairId = 1
let nextDirectId = 1

// TINA's trade/tariff data lags by ~2 years. Cap selectable years there
// and offer the 5 most recent.
const MAX_DATA_YEAR = new Date().getFullYear() - 2
const YEAR_OPTIONS = Array.from({length: 5}, (_, i) => MAX_DATA_YEAR - i)

// Persists the staged scenario (pairs + direct shocks + closure/method) AND
// the live commodity table (reporter/partner/year + fetched rows, including
// any unstaged edits) across reloads, same as CGEShockForm's
// SCENARIO_STORAGE_KEY does for the direct sector-shock tab.
const SCENARIO_STORAGE_KEY = 'cge-commodity-shock-scenario'

type FetchedFor = {reporter: CGETariffEntity; partner: CGETariffEntity; year: number}

type SavedCommodityScenario = {
  pairs: StagedPair[]
  directShocks: DirectShockRow[]
  closure: string
  method: 'johansen' | 'gragg'
  reporter: CGETariffEntity
  partner: CGETariffEntity
  year: number
  rows: CommodityRowState[]
  // What the saved `rows` were actually fetched for. May differ from
  // reporter/partner/year above if the user changed a dropdown but never
  // clicked Load again before leaving.
  fetchedFor: FetchedFor | null
}

const loadSavedScenario = (): SavedCommodityScenario | null => {
  try {
    const raw = localStorage.getItem(SCENARIO_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed?.pairs) || !Array.isArray(parsed?.directShocks)) return null
    return parsed as SavedCommodityScenario
  } catch (error) {
    console.error('CGE COMMODITY SCENARIO LOCAL STORAGE PARSE ERROR', error)
    return null
  }
}

const STAGED_IMPACT_ANCHOR_ID = 'cge-staged-scenario-impact'
const STAGED_IMPACT_SKELETON_ROW_COUNT = 3

const columnHelper = createColumnHelper<CommodityRowState>()

const PERM_CGE_HEAVY_COMPUTE = 'PERM_CGE_HEAVY_COMPUTE'

const CGECommodityShockForm: FC<CGECommodityShockFormProps> = ({metadata, onSubmit}) => {
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)
  const canRunHeavyCompute = ability.can('approve', PERM_CGE_HEAVY_COMPUTE)

  const [reporter, setReporter] = useState<CGETariffEntity>(
    () => loadSavedScenario()?.reporter ?? 'BGD'
  )
  const [partner, setPartner] = useState<CGETariffEntity>(
    () => loadSavedScenario()?.partner ?? 'USA'
  )
  const [year, setYear] = useState<number>(() => loadSavedScenario()?.year ?? MAX_DATA_YEAR)
  const [rows, setRows] = useState<CommodityRowState[]>(() => loadSavedScenario()?.rows ?? [])
  const [fetchedFor, setFetchedFor] = useState<FetchedFor | null>(
    () => loadSavedScenario()?.fetchedFor ?? null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [sectorFilter, setSectorFilter] = useState('')
  const [selectedHs, setSelectedHs] = useState<Set<string>>(new Set())
  const [bulkValue, setBulkValue] = useState<number>(0)

  const [pairs, setPairs] = useState<StagedPair[]>(() => {
    const saved = loadSavedScenario()
    if (saved) nextPairId = Math.max(...saved.pairs.map((p) => p.id), 0) + 1
    return saved?.pairs ?? []
  })
  const [directShocks, setDirectShocks] = useState<DirectShockRow[]>(() => {
    const saved = loadSavedScenario()
    if (saved) nextDirectId = Math.max(...saved.directShocks.map((r) => r.id), 0) + 1
    return saved?.directShocks ?? []
  })

  const [closure, setClosure] = useState<string>(() => loadSavedScenario()?.closure ?? 'standard')
  const [method, setMethod] = useState<'johansen' | 'gragg'>(() => {
    const saved = loadSavedScenario()?.method ?? 'johansen'
    return saved === 'gragg' && !canRunHeavyCompute ? 'johansen' : saved
  })

  const [tariffEntities, setTariffEntities] = useState<CGETariffEntitiesResponse | null>(null)
  useEffect(() => {
    getCGETariffEntities(axiosPrivate)
      .then((res) => setTariffEntities(res.data))
      .catch((e) => console.error('CGE TARIFF ENTITIES FETCH ERROR', e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const seasiaIso3s = useMemo(
    () => new Set((tariffEntities?.seasia ?? []).map((m) => m.iso3)),
    [tariffEntities]
  )
  // Core labels are curated by hand above; seasia/row members fall back to
  // the display name TINA already has for that economy.
  const memberLabelByIso3 = useMemo(() => {
    const map = new Map<string, string>()
    ;[...(tariffEntities?.seasia ?? []), ...(tariffEntities?.row ?? [])].forEach((m) =>
      map.set(m.iso3, m.name)
    )
    return map
  }, [tariffEntities])
  const labelFor = (iso3: string) => ENTITY_LABELS[iso3] ?? memberLabelByIso3.get(iso3) ?? iso3

  // Engine region codes (e.g. 'bgd', 'usa', 'row') as used in CGESectorRollup
  // source/dest, display-only, distinct from labelFor's ISO3 tariff-entity
  // codes above.
  const engineSectorName = (code?: string) =>
    (code && metadata?.sectors.find((s) => s.code === code)?.name) ?? code ?? ''
  const engineRegionName = (code?: string) =>
    (code && metadata?.regions.find((r) => r.code === code)?.name) ?? code ?? ''

  const reporterSections = useMemo<RegionSection[]>(
    () => [
      {label: 'Core economies', options: TARIFF_ENTITY_OPTIONS},
      {
        label: 'South East Asia',
        options: (tariffEntities?.seasia ?? []).map((m) => ({code: m.iso3, name: m.name})),
      },
      {
        label: 'Rest of World',
        options: (tariffEntities?.row ?? []).map((m) => ({code: m.iso3, name: m.name})),
      },
    ],
    [tariffEntities]
  )
  // Partner can't be from the same resolved CGE region as reporter, e.g.
  // Indonesia + Vietnam are both `seasia`, which isn't a valid pair (see
  // backend's resolveGtapRegion "must resolve to different regions" check).
  const partnerSections = useMemo<RegionSection[]>(() => {
    const reporterGroup = regionGroupKey(reporter, seasiaIso3s)
    return reporterSections.map((s) => ({
      ...s,
      options: s.options.filter((o) => regionGroupKey(o.code, seasiaIso3s) !== reporterGroup),
    }))
  }, [reporterSections, reporter, seasiaIso3s])

  // True once reporter or partner resolves to an aggregate region. The
  // submitted/previewed shock then reflects the whole bloc (every South East
  // Asia or Rest of World member's trade-weighted position), not just this
  // one country. See buildRegionShocks server-side.
  const isAggregatePair =
    regionGroupKey(reporter, seasiaIso3s) === 'seasia' ||
    regionGroupKey(reporter, seasiaIso3s) === 'row' ||
    regionGroupKey(partner, seasiaIso3s) === 'seasia' ||
    regionGroupKey(partner, seasiaIso3s) === 'row'

  useEffect(() => {
    try {
      localStorage.setItem(
        SCENARIO_STORAGE_KEY,
        JSON.stringify({
          pairs,
          directShocks,
          closure,
          method,
          reporter,
          partner,
          year,
          rows,
          fetchedFor,
        })
      )
    } catch (error) {
      console.error('CGE COMMODITY SCENARIO LOCAL STORAGE SAVE ERROR', error)
    }
  }, [pairs, directShocks, closure, method, reporter, partner, year, rows, fetchedFor])

  // Selection changed since the table was last fetched. What's shown no
  // longer matches reporter/partner/year, so old/new rates and the rollup
  // would be wrong if added to the scenario as-is.
  const isStale =
    !!fetchedFor &&
    (fetchedFor.reporter !== reporter || fetchedFor.partner !== partner || fetchedFor.year !== year)

  const fetchTable = () => {
    if (regionGroupKey(reporter, seasiaIso3s) === regionGroupKey(partner, seasiaIso3s)) {
      Toast(
        'Invalid selection',
        'warning',
        'Reporter and partner must resolve to different CGE regions',
        false
      )
      return
    }
    setIsLoading(true)
    setLoadError(null)
    setSelectedHs(new Set())
    getCGECommodityTariffs(axiosPrivate, {reporter, partner, year})
      .then((res) => {
        setRows(res.data.data.map((r) => ({...r, new_rate_pct: r.old_rate_pct})))
        setFetchedFor({reporter, partner, year})
        if (res.data.data.length === 0) {
          Toast(
            'No data found',
            'warning',
            `No commodity trade data for ${labelFor(reporter)} → ${labelFor(
              partner
            )} in ${year}. Try a different year or partner.`,
            false
          )
        }
      })
      .catch((e) => {
        console.error(e)
        setLoadError(e?.response?.data?.message || e.message)
        setRows([])
      })
      .finally(() => setIsLoading(false))
  }

  // Skip the auto-fetch if a table was already restored from localStorage.
  // don't clobber restored edits with a fresh (unedited) fetch on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (rows.length === 0) fetchTable()
  }, [])

  const sectorOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.sector_label).filter(Boolean))).sort() as string[],
    [rows]
  )
  const unclassifiedCount = useMemo(() => rows.filter((r) => !r.sector_code).length, [rows])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((r) => {
      if (sectorFilter && r.sector_label !== sectorFilter) return false
      if (!q) return true
      return r.hs.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q)
    })
  }, [rows, search, sectorFilter])

  const updateRowRate = (hs: string, value: number) => {
    setRows((prev) => prev.map((r) => (r.hs === hs ? {...r, new_rate_pct: value} : r)))
  }

  const handleRowDataChange = (rowId: string, columnId: string, value: any) => {
    if (columnId !== 'new_rate_pct') return
    const row = filteredRows[Number(rowId)]
    if (!row) return
    const parsed = typeof value === 'number' ? value : parseFloat(value)
    if (isNaN(parsed)) return
    updateRowRate(row.hs, parsed)
  }

  const toggleSelected = (hs: string) => {
    setSelectedHs((prev) => {
      const next = new Set(prev)
      next.has(hs) ? next.delete(hs) : next.add(hs)
      return next
    })
  }
  const selectAllFiltered = () => setSelectedHs(new Set(filteredRows.map((r) => r.hs)))
  const clearSelection = () => setSelectedHs(new Set())

  const scrollToStagedImpact = () =>
    document
      .getElementById(STAGED_IMPACT_ANCHOR_ID)
      ?.scrollIntoView({behavior: 'smooth', block: 'start'})

  const applyBulk = () => {
    if (selectedHs.size === 0) {
      Toast(
        'Nothing selected',
        'warning',
        'Select rows (or "select all matching filter") first',
        false
      )
      return
    }
    setRows((prev) => prev.map((r) => (selectedHs.has(r.hs) ? {...r, new_rate_pct: bulkValue} : r)))
  }

  const touchedCount = useMemo(
    () => rows.filter((r) => r.new_rate_pct !== r.old_rate_pct).length,
    [rows]
  )

  const addPairToScenario = () => {
    const touched_rows = rows
      .filter((r) => r.new_rate_pct !== r.old_rate_pct)
      .map((r) => ({hs: r.hs, new_rate_pct: r.new_rate_pct}))
    if (touched_rows.length === 0) {
      Toast('No changes to add', 'warning', 'Edit at least one commodity rate first', false)
      return
    }
    setPairs((prev) => [
      ...prev.filter((p) => !(p.reporter === reporter && p.partner === partner && p.year === year)),
      {id: nextPairId++, reporter, partner, year, touched_rows},
    ])
    Toast(
      'Added to scenario',
      'success',
      `${touched_rows.length} commodity change(s) for ${labelFor(reporter)} → ${labelFor(partner)}`,
      false
    )
  }
  const removePair = (id: number) => setPairs((prev) => prev.filter((p) => p.id !== id))

  // Real combined region shock (all South East Asia / Rest of World members
  // weighted together server-side, see buildRegionShocks), refreshed
  // whenever the staged pairs change. Runs DB-only, no Julia /simulate call,
  // so it's fast enough to show before the user commits to a full run.
  const [rollupPreview, setRollupPreview] = useState<CGESectorRollup[]>([])
  const [rollupPreviewLoading, setRollupPreviewLoading] = useState(false)
  useEffect(() => {
    if (pairs.length === 0 || !canRunHeavyCompute) {
      setRollupPreview([])
      return
    }
    setRollupPreviewLoading(true)
    getCGECommodityRollupPreview(
      axiosPrivate,
      pairs.map(({reporter, partner, year, touched_rows}) => ({
        reporter,
        partner,
        year,
        touched_rows,
      }))
    )
      .then((res) => setRollupPreview(res.data.sector_rollup))
      .catch((e) => console.error('CGE COMMODITY ROLLUP PREVIEW ERROR', e))
      .finally(() => setRollupPreviewLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs])

  const addDirectShock = () =>
    setDirectShocks((prev) => [
      ...prev,
      {
        id: nextDirectId++,
        good: metadata?.sectors[0]?.code ?? '',
        source: 'row',
        dest: 'bgd',
        tms_power_change_pct: 10,
      },
    ])
  const updateDirectShock = (id: number, field: keyof DirectShockRow, value: string | number) =>
    setDirectShocks((prev) => prev.map((r) => (r.id === id ? {...r, [field]: value} : r)))
  const removeDirectShock = (id: number) =>
    setDirectShocks((prev) => prev.filter((r) => r.id !== id))

  const rollup = useMemo(() => clientRollup(rows), [rows])

  const canRun = pairs.length > 0 || directShocks.length > 0
  const resetScenario = () => {
    setPairs([])
    setDirectShocks([])
    Toast('Scenario reset', 'success', 'Staged pairs and direct shocks cleared', false)
  }
  const handleSubmit = () => {
    onSubmit({
      closure,
      method,
      pairs: pairs.map(({reporter, partner, year, touched_rows}) => ({
        reporter,
        partner,
        year,
        touched_rows,
      })),
      direct_shocks: directShocks.map(({good, source, dest, tms_power_change_pct}) => ({
        good,
        source,
        dest,
        tms_power_change_pct,
      })),
    })
  }

  const columns = useMemo<ColumnDef<CommodityRowState, any>[]>(
    () => [
      columnHelper.display({
        id: 'select',
        header: () => (
          <Form.Check
            type='checkbox'
            checked={filteredRows.length > 0 && filteredRows.every((r) => selectedHs.has(r.hs))}
            onChange={() =>
              filteredRows.every((r) => selectedHs.has(r.hs))
                ? clearSelection()
                : selectAllFiltered()
            }
            title='Select / clear all rows matching current filter'
          />
        ),
        cell: (info) => (
          <Form.Check
            type='checkbox'
            checked={selectedHs.has(info.row.original.hs)}
            onChange={() => toggleSelected(info.row.original.hs)}
          />
        ),
        meta: {headerClass: 'w-40px text-center', cellClass: 'text-center'},
      }),
      columnHelper.accessor('hs', {
        header: 'HS code',
        cell: (info) => <span className='fw-bold'>{info.getValue()}</span>,
        meta: {headerClass: 'w-100px', cellClass: ''},
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => <small title={info.getValue() ?? ''}>{info.getValue() ?? '.'}</small>,
        meta: {headerClass: '', cellClass: ''},
      }),
      columnHelper.accessor('sector_label', {
        header: 'Sector',
        cell: (info) => {
          const label = info.getValue()
          if (!label) return <Badge bg='secondary'>unclassified</Badge>
          return (
            <Badge bg='light' text='dark' title={info.row.original.sector_code ?? ''}>
              {label}
            </Badge>
          )
        },
        meta: {headerClass: 'w-200px', cellClass: ''},
      }),
      columnHelper.accessor('export_value', {
        header: 'Export value (USD)',
        cell: (info) => formatNumber(info.getValue()),
        meta: {headerClass: 'w-120px text-end', cellClass: 'text-end'},
      }),
      columnHelper.accessor('old_rate_pct', {
        header: 'Old rate %',
        cell: (info) => (
          <span
            title={info.row.original.no_tariff_data ? 'No tariff data found. Defaulted to 0%' : ''}
          >
            {formatNumber(info.getValue())}
            {info.row.original.no_tariff_data && (
              <i className='fa-solid fa-circle-question text-muted ms-1'></i>
            )}
          </span>
        ),
        meta: {headerClass: 'w-100px text-end', cellClass: 'text-end'},
      }),
      columnHelper.accessor('new_rate_pct', {
        header: 'New rate %',
        cell: (info) => info.getValue(),
        meta: {headerClass: 'w-120px text-end', cellClass: 'text-end', isEditable: true},
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRows, selectedHs, metadata]
  )

  return (
    <Card>
      <style>{`
        .cge-commodity-table {
          max-height: 600px;
          overflow-y: auto;
        }
        .cge-commodity-table table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
        }
      `}</style>
      <Card.Header className='card-header-stretch sticky-top bg-white' style={{zIndex: 10}}>
        <Card.Title className='w-100 d-flex justify-content-between align-items-center flex-wrap gap-3'>
          <span>
            <span className='fa fa-gear px-2'></span>
            <h3 className='text-gray-700 d-inline'>Commodity-level tariff scenario</h3>
          </span>
          <span className='d-flex align-items-center gap-2 flex-wrap'>
            <span className='text-primary fw-bold'>Solver:</span>
            <Button
              variant=''
              size='sm'
              className={`fw-bold ${
                method === 'johansen' ? 'btn-primary text-white' : 'btn-outline btn-outline-primary'
              }`}
              onClick={() => setMethod('johansen')}
            >
              johansen
            </Button>
            <Button
              variant=''
              size='sm'
              className={`fw-bold ${
                method === 'gragg' ? 'btn-primary text-white' : 'btn-outline btn-outline-primary'
              }`}
              onClick={() => setMethod('gragg')}
              disabled={!canRunHeavyCompute}
              title={canRunHeavyCompute ? undefined : 'Requires additional permission'}
            >
              gragg
            </Button>
            <span className='text-primary fw-bold ms-3'>Closure:</span>
            {metadata?.closures.map((c) => (
              <Button
                key={c}
                variant=''
                size='sm'
                className={`fw-bold ${
                  closure === c ? 'btn-primary text-white' : 'btn-outline btn-outline-primary'
                }`}
                onClick={() => setClosure(c)}
              >
                {c}
              </Button>
            ))}
          </span>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <div className='d-flex align-items-center gap-3 mb-4'>
          <span className='fw-bold text-primary flex-shrink-0' style={{fontSize: '3.5rem'}}>
            1
          </span>
          <div>
            <h4 className='text-gray-800 mb-1'>
              <i className='fa-solid fa-table-list me-2 text-primary'></i>
              Build tariff table
            </h4>
            <div className='text-muted fs-7'>
              Pick a reporter, partner and year, edit commodity rates below, then check the live
              sector rollup before staging it to the scenario.
            </div>
          </div>
        </div>

        <Row className='align-items-end mb-4 gy-2'>
          <Col md={3}>
            <Form.Label className='fw-bold'>Reporter (exporter, faces the tariff)</Form.Label>
            <RegionDropdown
              sections={reporterSections}
              value={reporter}
              onChange={(code) => setReporter(code)}
            />
          </Col>
          <Col md={3}>
            <Form.Label className='fw-bold'>Partner (importer, sets the tariff)</Form.Label>
            <RegionDropdown
              sections={partnerSections}
              value={partner}
              onChange={(code) => setPartner(code)}
            />
          </Col>
          <Col md={2}>
            <Form.Label className='fw-bold'>Year</Form.Label>
            <Form.Select size='sm' value={year} onChange={(e) => setYear(+e.target.value)}>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Button
              variant=''
              className={`w-100 fw-bold ${
                isStale ? 'btn-warning text-black' : 'btn-outline btn-outline-primary'
              }`}
              size='sm'
              onClick={fetchTable}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner animation='border' size='sm' />
              ) : (
                <>
                  <i className={`fa-solid fa-rotate me-2 ${isStale ? 'text-black' : ''}`}></i>
                  Load
                </>
              )}
            </Button>
          </Col>
          <Col md={2}>
            <Button
              className='btn-warning text-black fw-bold w-100'
              size='sm'
              onClick={addPairToScenario}
              disabled={touchedCount === 0 || isStale}
              title={isStale ? 'Selection changed. Click Load first' : undefined}
            >
              Add to scenario ({touchedCount})
            </Button>
          </Col>
        </Row>

        {isStale && (
          <div className='alert alert-warning d-flex align-items-start'>
            <i className='fa-solid fa-triangle-exclamation me-2 mt-1'></i>
            <div>
              Reporter/partner/year changed since this table was loaded. Click{' '}
              <strong className='mx-1'>Load</strong>
              to refresh it before adding to the scenario.
            </div>
          </div>
        )}

        {isAggregatePair && (
          <div className='alert alert-primary d-flex align-items-start'>
            <i className='fa-solid fa-circle-info me-2 mt-1'></i>
            <div>
              {regionGroupKey(reporter, seasiaIso3s) === 'seasia' ||
              regionGroupKey(partner, seasiaIso3s) === 'seasia'
                ? 'South East Asia'
                : 'Rest of World'}{' '}
              is a GTAP aggregate of many countries. The table below previews only{' '}
              {labelFor(
                regionGroupKey(reporter, seasiaIso3s) === 'seasia' ||
                  regionGroupKey(reporter, seasiaIso3s) === 'row'
                  ? reporter
                  : partner
              )}
              's rates. The shock actually added to the scenario combines every member's current
              tariff, trade-weighted (see{' '}
              <span
                role='button'
                className='fw-bold text-decoration-underline'
                onClick={scrollToStagedImpact}
              >
                "Staged scenario impact"
              </span>{' '}
              below).
            </div>
          </div>
        )}

        {loadError && <div className='alert alert-danger'>{loadError}</div>}

        {rows.length > 0 && !isLoading && (
          <>
            <Row className='align-items-end mb-3 gy-2'>
              <Col md={3}>
                <Form.Label className='fw-bold'>Search</Form.Label>
                <Form.Control
                  size='sm'
                  placeholder='Search HS code or description...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label className='fw-bold'>Sector</Form.Label>
                <Form.Select
                  size='sm'
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                >
                  <option value=''>All sectors</option>
                  {sectorOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className='fw-bold'>Bulk new rate</Form.Label>
                <InputGroup size='sm'>
                  <Form.Control
                    type='number'
                    step={0.5}
                    value={bulkValue}
                    onChange={(e) => setBulkValue(+e.target.value)}
                    title='Bulk new rate %'
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>
              <Col md={4}>
                <Form.Label className='fw-bold'>Bulk edit selection</Form.Label>
                <InputGroup size='sm'>
                  <Button
                    variant=''
                    className='btn-outline btn-outline-primary'
                    size='sm'
                    onClick={applyBulk}
                    title='Apply the rate above to every selected row'
                  >
                    Apply to selected ({selectedHs.size})
                  </Button>
                  <Button
                    variant=''
                    className='btn-outline btn-outline-primary'
                    size='sm'
                    onClick={clearSelection}
                    disabled={selectedHs.size === 0}
                  >
                    Clear
                  </Button>
                </InputGroup>
              </Col>
            </Row>
            {unclassifiedCount > 0 && (
              <div className='text-muted small mb-2'>
                <i className='fa-solid fa-circle-info me-1'></i>
                {unclassifiedCount} HS line(s) are unclassified and excluded from the simulation.
              </div>
            )}

            <DataTable<CommodityRowState>
              data={filteredRows}
              columns={columns}
              enableGlobalFilter={false}
              onRowDataChange={handleRowDataChange}
              defaultPageSize={20}
              pageSizes={[20, 50, 100]}
              className='cge-commodity-table'
              initialSortBy={{id: 'export_value', desc: true}}
            />
          </>
        )}

        {rollup.length > 0 && (
          <div className='mt-4'>
            <h5 className='text-gray-700'>
              Live sector rollup ({labelFor(reporter)} → {labelFor(partner)}
              {isAggregatePair &&
                ' | preview only, see "Staged scenario impact" for the combined region shock'}
              )
            </h5>
            <Table size='sm' bordered className='align-middle'>
              <thead className='table-light'>
                <tr>
                  <th>Sector</th>
                  <th className='text-end'>Export value (USD)</th>
                  <th className='text-end'>Old rate % (wtd avg)</th>
                  <th className='text-end'>New rate % (wtd avg)</th>
                  <th className='text-end'>tms power change %</th>
                </tr>
              </thead>
              <tbody>
                {rollup.map((r) => (
                  <tr key={r.sector}>
                    <td>{r.sector_label}</td>
                    <td className='text-end'>{formatNumber(r.export_value)}</td>
                    <td className='text-end'>{formatNumber(+r.old_rate_pct.toFixed(2))}</td>
                    <td className='text-end'>{formatNumber(+r.new_rate_pct.toFixed(2))}</td>
                    <td
                      className={`text-end fw-bold ${
                        r.tms_power_change_pct < 0
                          ? 'text-success'
                          : r.tms_power_change_pct > 0
                          ? 'text-danger'
                          : ''
                      }`}
                    >
                      {r.tms_power_change_pct > 0 ? '+' : ''}
                      {formatNumber(+r.tms_power_change_pct.toFixed(2))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <hr className='my-6 border-2' />
        <div className='d-flex align-items-center gap-3 mb-4'>
          <span className='fw-bold text-primary flex-shrink-0' style={{fontSize: '3.5rem'}}>
            2
          </span>
          <div>
            <h4 className='text-gray-800 mb-1'>
              <i className='fa-solid fa-layer-group me-2 text-primary'></i>
              Scenario
            </h4>
            <div className='text-muted fs-7'>
              Everything below is what actually gets submitted when you run the simulation.
            </div>
          </div>
        </div>

        {pairs.length > 0 && (
          <div className='mt-4'>
            <h5 className='text-gray-700'>Staged scenario</h5>
            <div className='d-flex flex-wrap gap-2'>
              {pairs.map((p) => (
                <Badge
                  key={p.id}
                  bg='light'
                  text='dark'
                  className='p-2 border d-flex align-items-center gap-2'
                >
                  {labelFor(p.reporter)} → {labelFor(p.partner)} ({p.year}) |{' '}
                  {p.touched_rows.length} line(s)
                  <i
                    className='fa-solid fa-xmark cursor-pointer'
                    onClick={() => removePair(p.id)}
                  ></i>
                </Badge>
              ))}
            </div>

            <h5 id={STAGED_IMPACT_ANCHOR_ID} className='text-gray-700 mt-3'>
              Staged scenario impact
            </h5>
            <div className='text-muted small mb-2'>
              The actual combined shock per CGE region pair. For South East Asia / Rest of World
              this includes every bloc member's current tariff, trade-weighted.
            </div>
            {!canRunHeavyCompute ? (
              <div className='alert alert-warning text-dark d-flex align-items-start mb-0'>
                <i className='fa-solid fa-lock me-2 mt-1'></i>
                <div>
                  Staged scenario impact preview requires additional permission. The simulation will
                  still run with the pairs staged above.
                </div>
              </div>
            ) : (
              <div className='cge-scroll-table'>
                <Table size='sm' bordered className='align-middle mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th>Sector</th>
                      <th>Region pair</th>
                      <th className='text-end'>Old rate % (wtd avg)</th>
                      <th className='text-end'>New rate % (wtd avg)</th>
                      <th className='text-end'>tms power change %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rollupPreviewLoading
                      ? Array.from({length: STAGED_IMPACT_SKELETON_ROW_COUNT}).map((_, i) => (
                          <tr key={`staged-impact-skeleton-${i}`} className='placeholder-glow'>
                            <td>
                              <span className='placeholder col-8'></span>
                            </td>
                            <td>
                              <span className='placeholder col-6'></span>
                            </td>
                            <td className='text-end'>
                              <span className='placeholder col-4'></span>
                            </td>
                            <td className='text-end'>
                              <span className='placeholder col-4'></span>
                            </td>
                            <td className='text-end'>
                              <span className='placeholder col-4'></span>
                            </td>
                          </tr>
                        ))
                      : rollupPreview.map((r) => (
                          <tr key={`${r.sector}-${r.source}-${r.dest}`}>
                            <td>
                              {engineSectorName(r.sector)} [{r.sector}]
                            </td>
                            <td>
                              <span className='d-inline-flex align-items-center'>
                                {r.source && regionFlagIcon(r.source)}
                                {engineRegionName(r.source)}
                              </span>
                              {' → '}
                              <span className='d-inline-flex align-items-center'>
                                {r.dest && regionFlagIcon(r.dest)}
                                {engineRegionName(r.dest)}
                              </span>
                            </td>
                            <td className='text-end'>{formatNumber(+r.old_rate_pct.toFixed(2))}</td>
                            <td className='text-end'>{formatNumber(+r.new_rate_pct.toFixed(2))}</td>
                            <td
                              className={`text-end fw-bold ${
                                r.tms_power_change_pct < 0
                                  ? 'text-success'
                                  : r.tms_power_change_pct > 0
                                  ? 'text-danger'
                                  : ''
                              }`}
                            >
                              {r.tms_power_change_pct > 0 ? '+' : ''}
                              {formatNumber(+r.tms_power_change_pct.toFixed(2))}
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}

        <div className='mt-4'>
          <h5 className='text-gray-700 d-flex justify-content-between align-items-center'>
            Additional direct shocks (e.g. row, seasia | not addressable by commodity)
            <Button variant='outline' size='sm' onClick={addDirectShock}>
              <i className='fa-solid fa-plus me-2'></i>Add
            </Button>
          </h5>
          {directShocks.length > 0 && (
            <Table size='sm' className='align-middle'>
              <thead>
                <tr>
                  <th>Sector</th>
                  <th>Exporting region</th>
                  <th>Importing region</th>
                  <th>tms power change %</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {directShocks.map((r) => (
                  <tr key={r.id}>
                    <td style={{minWidth: 150}}>
                      <Form.Select
                        size='sm'
                        value={r.good}
                        onChange={(e) => updateDirectShock(r.id, 'good', e.target.value)}
                      >
                        {metadata?.sectors.map((s) => (
                          <option key={s.code} value={s.code}>
                            {s.name}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td style={{minWidth: 170}}>
                      <RegionDropdown
                        options={metadata?.regions ?? []}
                        value={r.source}
                        onChange={(code) => updateDirectShock(r.id, 'source', code)}
                      />
                    </td>
                    <td style={{minWidth: 170}}>
                      <RegionDropdown
                        options={(metadata?.regions ?? []).filter((reg) => reg.code !== r.source)}
                        value={r.dest}
                        onChange={(code) => updateDirectShock(r.id, 'dest', code)}
                      />
                    </td>
                    <td style={{minWidth: 100}}>
                      <Form.Control
                        size='sm'
                        type='number'
                        step={0.5}
                        value={r.tms_power_change_pct}
                        onChange={(e) =>
                          updateDirectShock(r.id, 'tms_power_change_pct', +e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Button
                        variant='outline'
                        size='sm'
                        className='text-danger'
                        onClick={() => removeDirectShock(r.id)}
                      >
                        <i className='fa-solid fa-trash'></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <Row className='mt-4'>
          <Col className='d-flex justify-content-end gap-2'>
            <Button
              variant=''
              className='fw-bold btn-outline btn-outline-primary'
              disabled={!canRun}
              onClick={resetScenario}
              title='Clear all staged pairs and direct shocks'
            >
              <i className='fa-solid fa-rotate-left me-2'></i>
              Reset scenario
            </Button>
            <Button
              className='btn btn-warning text-black fw-bold'
              disabled={!canRun || rollupPreviewLoading}
              title={
                rollupPreviewLoading ? 'Staged scenario impact still calculating...' : undefined
              }
              onClick={handleSubmit}
            >
              {rollupPreviewLoading ? (
                <Spinner animation='border' size='sm' />
              ) : (
                <>
                  Run simulation
                  <span className='fa-solid fa-angles-right px-1'></span>
                </>
              )}
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

export {CGECommodityShockForm}
